import {
  effectivePaymentSourceOf,
  settlementSourcesOf,
  type CashFlowSource,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { settlementSourceMeta } from '@/lib/settlementSource';
import { formatEtDate, type CashFlowEntry } from '@/lib/cashFlow';

/**
 * A compact settlement-source badge (short code + status dot), styled from the
 * shared {@link settlementSourceMeta} so it reads identically wherever it appears
 * (the register column, the detail overview — L14, master rule).
 */
export function SettlementSourceBadge({ source, title }: { source: CashFlowSource; title?: string }) {
  const meta = settlementSourceMeta(source);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold',
        meta.badge,
      )}
      title={title}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} aria-hidden />
      {meta.short}
    </span>
  );
}

/**
 * The register `Allikas` column cell: the entry's settlement TRUTH source as a
 * compact badge, with a "+N" cue when lower-priority sources also reported cash
 * (kept for audit, superseded by the truth). The truth + the set of cash sources
 * come from finance-core (`effectivePaymentSourceOf` / `settlementSourcesOf`) —
 * the cell never re-derives the ranking (master rule; L9). An unsettled entry
 * shows a muted dash.
 */
export function SettlementSourceCell({ entry }: { entry: CashFlowEntry }) {
  const truth = effectivePaymentSourceOf(entry);
  if (!truth) return <span className="text-muted-foreground/40">—</span>;
  const cashSources = settlementSourcesOf(entry).filter((s) => s.paidCents !== 0);
  const meta = settlementSourceMeta(truth.source);
  return (
    <div className="flex items-center justify-end gap-1.5">
      {cashSources.length > 1 && (
        <span
          className="text-[9px] tabular-nums text-muted-foreground/50"
          title={`${cashSources.length} allikat · madalamad ületatud`}
        >
          +{cashSources.length - 1}
        </span>
      )}
      <SettlementSourceBadge
        source={truth.source}
        title={`Tõeallikas: ${meta.label}${truth.latestPaidOn ? ` · ${formatEtDate(truth.latestPaidOn)}` : ''}`}
      />
    </div>
  );
}
